import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { AuthContext } from '../../../Contexts/AuthContext';
import FormDialog from '../../../Components/Modal/modal';
import UseAxios from '../../../Api/http/http';
import {
  getInvoicePaymentStatus,
  createInvoicePaymentStatus,
  updateInvoicePaymentStatus,
} from '../../../Api/request/invoicePaymentStatus';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { invoicePaymentStatusTableConfig } from './invoicePaymentStatusTableConfig';
import './InvoicePaymentStatus.scss';

const EMPTY_FORM = { name: '' };
const EMPTY_PERMISSIONS = [];

const getApiErrorMessage = (response) => {
  const payload = response?.data;
  if (payload instanceof Error) {
    return payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || payload.message
      || 'Something went wrong.';
  }
  if (payload?.status === false) {
    return payload?.message
      || payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || 'Something went wrong.';
  }
  return '';
};

const normalizeInvoicePaymentStatusLists = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : response?.data?.data || response?.data?.invoice || [];

  return rows.map((invoicePaymentStatus) => normalizeInvoicePaymentStatus(invoicePaymentStatus));
};

const normalizeInvoicePaymentStatus = (invoicePaymentStatus) => ({
  id: invoicePaymentStatus.payment_status_id,
  name: invoicePaymentStatus.payment_status_name || '',
  isActive: Boolean(invoicePaymentStatus.is_active),
});

const InvoicePaymentStatusForm = ({ mode, form, error, isSubmitDisabled, onChange, onClose, onSubmit }) => createPortal(
  <div className="invoice-modal-backdrop">
    <form className="invoice-modal" onSubmit={onSubmit}>
      <div className="invoice-modal__header">
        {mode === 'edit' ? 'Edit Invoice Payment Status' : 'Add Invoice Payment Status'}
      </div>
      <div className="invoice-modal__body">
        <label htmlFor="department-name" className={error ? 'is-error' : ''}>
          Invoice Payment Status Name<span className="required-asterisk">*</span>
        </label>
        <input
          id="department-name"
          name="name"
          type="text"
          placeholder="Enter Invoice Payment Status Name"
          value={form.name}
          onChange={onChange}
          className={error ? 'is-error' : ''}
          autoComplete="off"
        />
      </div>
      <div className="invoice-modal__footer">
        <button type="button" className="invoice-modal__btn invoice-modal__btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="invoice-modal__btn invoice-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const InvoicePaymentStatus = () => {
  const { userProfile } = React.useContext(AuthContext);
  const permission = userProfile?.permissions?.[MODULE_NAMES.CONFIGURATION_INVOICE_PAYMENT_STATUS];
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );
  const normalizedPermissions = useMemo(
    () => (permission || EMPTY_PERMISSIONS).map((action) => String(action).toLowerCase()),
    [permission],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
    };
    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);
  const [ invoicePaymentStatuses, setInvoicePaymentStatus ] = useState([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, mode: 'add', id: null });
  const [toggleInvoice, setToggleInvoice] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasError, setHasError] = useState(false);

  const loadInvoicePaymentStatusList = useCallback(async () => {
    if (!hasAccess('read')) return;
    const response = await UseAxios(getInvoicePaymentStatus(), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    setInvoicePaymentStatus(normalizeInvoicePaymentStatusLists(response));
  }, [hasAccess]);

  useEffect(() => {
    loadInvoicePaymentStatusList();
  }, [loadInvoicePaymentStatusList]);

  const closeForm = useCallback(() => {
    setDialog({ isOpen: false, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, []);

  const openAdd = useCallback(() => {
    if (!hasAccess('create')) return;
    setDialog({ isOpen: true, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, [hasAccess]);

  const openEdit = useCallback((provider) => {
    if (!hasAccess('update')) return;
    setDialog({ isOpen: true, mode: 'edit', id: provider.id });
    setForm({ name: provider.name });
    setHasError(false);
  }, [hasAccess]);

  const selectedInvoicePaymentStatus = invoicePaymentStatuses.find((provider) => provider.id === dialog.id);
  const isSubmitDisabled = !form.name.trim()
    || (dialog.mode === 'edit' && form.name.trim() === selectedInvoicePaymentStatus?.name.trim());

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!hasAccess(dialog.mode === 'edit' ? 'update' : 'create')) return;
    const name = form.name.trim();
    if (!name) {
      setHasError(true);
      toast.error('Please enter a invoice payment status name.');
      return;
    }

    const response = dialog.mode === 'edit'
      ? await UseAxios(
        updateInvoicePaymentStatus(dialog.id, name, selectedInvoicePaymentStatus?.isActive ?? true),
        setIsLoading,
      )
      : await UseAxios(createInvoicePaymentStatus(name), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    toast.success(dialog.mode === 'edit'
      ? 'Invoice Payment Status updated successfully.'
      : 'Invoice Payment Status added successfully.');
    const savedInvoicePaymentStatus = normalizeInvoicePaymentStatus(response?.data?.data || response.data);
    setInvoicePaymentStatus((currentInvoices) => (
      dialog.mode === 'edit'
        ? currentInvoices.map((invoice) => (
          invoice.id === savedInvoicePaymentStatus.id ? savedInvoicePaymentStatus : invoice
        ))
        : [...currentInvoices, savedInvoicePaymentStatus]
    ));
    closeForm();
  }, [closeForm, dialog.id, dialog.mode, form.name, hasAccess, selectedInvoicePaymentStatus]);


  const handleToggle = useCallback((invoice) => {
    if (!hasAccess('update')) return;
    setToggleInvoice(invoice);
  }, [hasAccess]);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleInvoice(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleInvoice || !hasAccess('update')) return;

    const nextStatus = !toggleInvoice.isActive;
    const response = await UseAxios(
      updateInvoicePaymentStatus(toggleInvoice.id, toggleInvoice.name, nextStatus),
      setIsLoading,
    );
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setInvoicePaymentStatus((currentInvoices) => currentInvoices.map((invoice) => (
      invoice.id === toggleInvoice.id
        ? { ...invoice, isActive: nextStatus }
        : invoice
    )));
    toast.success(`${toggleInvoice.name} is now ${nextStatus ? 'active' : 'inactive'}.`);
    handleCloseToggleDialog();
  }, [handleCloseToggleDialog, hasAccess, toggleInvoice]);

  const tableData = useMemo(() => invoicePaymentStatuses.map((invoice, index) => ({
    ...invoice,
    slNo: index + 1,
    invoicePaymentStatus: invoice.name,
    status: invoice.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="invoice-list-actions" onClick={(event) => event.stopPropagation()}>
        <ControlledSwitches
          checked={invoice.isActive}
          disabled={!hasAccess('update')}
          switchCss="switchCss"
          handleChange={() => handleToggle(invoice)}
        />
        <button
          type="button"
          className="invoice-list-action-btn"
          title="Edit Invoice Payment Status"
          aria-label={`Edit ${invoice.payment_status_name}`}
          disabled={!hasAccess('update')}
          onClick={() => openEdit(invoice)}
        >
          <img src={EditActionIcon} alt="" />
        </button>
      </div>
    ),
  })), [handleToggle, hasAccess, openEdit, invoicePaymentStatuses]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {},
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Invoice Payment Status',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: invoicePaymentStatusTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: invoicePaymentStatusTableConfig.length },
      paginationConfig: { page: 1, limit: tableData.length, total: tableData.length, totalPages: 1 },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length]);

  if (!hasAccess('read')) {
    return <div style={{marginTop: '30px'}}>You do not have permission to view invoice payment status.</div>;
  }

  return (
    <div className="invoice-list-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && (
        <InvoicePaymentStatusForm
          mode={dialog.mode}
          form={form}
          error={hasError}
          isSubmitDisabled={isSubmitDisabled}
          onChange={(event) => {
            setForm({ name: event.target.value });
            setHasError(false);
          }}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}
      <FormDialog
        open={Boolean(toggleInvoice)}
        title={`${toggleInvoice?.isActive ? 'Disable' : 'Enable'} Invoice Payment Status`}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {`Are you sure you want to ${toggleInvoice?.isActive
                ? 'disable' : 'enable'} this invoice payment status?`}
            </div>
          </div>
        )}
        button1="Cancel"
        cancelButtonId="cancel-cloud-provider-toggle"
        button2={toggleInvoice?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="confirm-cloud-provider-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default InvoicePaymentStatus;
